import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    Box,
    ClickAwayListener,
    IconButton,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
    type TypographyProps,
} from '@mui/material';
import { FaOptionIcon, informationIcon } from '@europrocurement/l2d-icons';

export type FlexyFormTooltipProps = {
    name: undefined | string;
    text: React.ReactNode;
};

export type FlexyFormLabelProps = TypographyProps & {
    children: React.ReactNode;
    required?: boolean;
    requiredTooltip?: TooltipProps['title'];
    tooltip?: FlexyFormTooltipProps;
    subLabel?: string;
};

const LightTooltip = styled(({ className, ...props }: TooltipProps) =>
    props.onClose ? (
        <ClickAwayListener onClickAway={props.onClose}>
            <Tooltip
                {...props}
                classes={{ popper: className }}
                PopperProps={{ sx: { '.MuiTooltip-tooltip': { bgcolor: 'green' } } }}
            />
        </ClickAwayListener>
    ) : (
        <Tooltip
            {...props}
            classes={{ popper: className }}
            PopperProps={{ sx: { '.MuiTooltip-tooltip': { bgcolor: 'green' } } }}
        />
    ),
)(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: `1px 1px 15px 0px ${theme.palette.background.shadow}`,
        textAlign: 'justify',
        padding: '15px',
        marginBottom: '5px',
    },
}));

/**
 * Composant permettant d'afficher un label personnalisé
 *
 *  ## Utilisation
 *
 * ```jsx
 * <FlexyFormLabel>
 *      mon label
 * </FlexyFormLabel>
 * ```
 */
export const FlexyFormLabel: React.FunctionComponent<FlexyFormLabelProps> = function ({
    children,
    tooltip,
    required,
    subLabel = '',
    requiredTooltip,
    ...rest
}) {
    const hasSubLabel = subLabel && subLabel !== '';
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleTooltipClose = () => {
        setTooltipOpen(false);
    };

    const handleTooltipToggle = () => {
        setTooltipOpen(!tooltipOpen);
    };

    return (
        <>
            <Box
                sx={{
                    marginBottom: '5px',
                    marginTop: '15px',
                    display: 'block',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant="l2dlabel"
                    {...rest}
                >
                    {children}
                    {required ? (
                        // {!required ? (
                        <Tooltip title={requiredTooltip || 'Le champ est requis'}>
                            {/* <Typography
                                component="span"
                                sx={{ opacity: 0.5, marginLeft: '4px' }}
                            >
                                (facultatif)
                            </Typography> */}
                            <Typography
                                component="span"
                                color="danger.main"
                            >
                                &nbsp;&nbsp;*
                            </Typography>
                        </Tooltip>
                    ) : null}
                </Typography>
                {tooltip ? (
                    <LightTooltip
                        key={tooltip.name}
                        title={
                            <Typography
                                variant="l2dbodyxs"
                                color="text.primary"
                                align="justify"
                            >
                                {tooltip.text}
                            </Typography>
                        }
                        open={tooltipOpen}
                        onClose={handleTooltipClose}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        placement="top-end"
                        sx={{
                            marginTop: '0px',
                        }}
                    >
                        <IconButton
                            color="secondary"
                            size="small"
                            sx={{
                                height: 'fit-content',
                                marginTop: '-10px',
                            }}
                            onClick={handleTooltipToggle}
                        >
                            <FaOptionIcon {...informationIcon.props} />
                        </IconButton>
                    </LightTooltip>
                ) : null}
            </Box>
            {hasSubLabel && (
                <Typography
                    color="text.secondary"
                    variant="l2dlabel"
                >
                    {subLabel}
                </Typography>
            )}
        </>
    );
};
